import { User, UserManager, UserManagerSettings } from 'oidc-client-ts';

import AuthorizationModuleFacade
  from 'shared/src/modules/authModule/store/authorizationModule.facade';
import { AuthErrorCode } from 'shared/src/models/authErrorCode';
import { Profile } from 'shared/src/modules/authModule/services/Authorization.contracts';
import { UserClient } from 'shared/clients/client-UserGroup';
import { LogoutType } from 'shared/clients/client-spa-AccountGroup';
import { sendExceptionEvent } from 'shared/src/utils/appInsights';
import { useLanguage } from 'shared/src/composables/useLanguage.composable';
import { INITIAL_LANGUAGE } from 'shared/src/modules/languageModule/consts';
import { getLanguageSubtagFromBCP47LanguageTag } from 'shared/src/modules/languageModule/helpers/language.helpers';

import { SeverityLevel } from '@microsoft/applicationinsights-common';

import { getQueryVariable } from '../helpers';
import { axiosInstance } from '../apiConfig';
import { DATAHUB_API } from '../urlConfig';

export interface AuthorizationResult {
  success: boolean;
  profile?: Profile;
  access_token?: string;
}

const enum redirectKey {
  redirectUrl = 'redirectUrl',
}

const userClient: UserClient = new UserClient(DATAHUB_API, axiosInstance);

const { storeLanguage } = useLanguage();

export default class StandardAuthorizationService {
  redirectUrl = `${window.location.protocol}//${window.location.host}`;
  settings: UserManagerSettings = {
    authority: window.appSettings.Sso.Authority,
    client_id: window.appSettings.Sso.ClientId,
    scope: window.appSettings.Sso.Scopes,
    redirect_uri: `${this.redirectUrl}`,
    response_type: 'code',
    silent_redirect_uri: `${this.redirectUrl}/silent-refresh`,
    automaticSilentRenew: true,
    accessTokenExpiringNotificationTimeInSeconds: 180,
    monitorSession: false,
    silentRequestTimeoutInSeconds: 12000,
    validateSubOnSilentRenew: true,
    filterProtocolClaims: true,
    revokeTokensOnSignout: false,
    popup_post_logout_redirect_uri: this.redirectUrl,
    popup_redirect_uri: this.redirectUrl,
    post_logout_redirect_uri: `${window.location.protocol}//${window.location.host}`,
  };

  userManager: UserManager;
  orgId = sessionStorage.getItem('orgId');

  constructor() {
    const settingsToAdd = this.orgId
      ? { ...this.settings, extraTokenParams: {
          org_id: this.orgId,
        }}
      : this.settings;

    this.userManager = new UserManager(settingsToAdd);
    this.userManager.events.addAccessTokenExpiring(() => {
      console.warn('token expiring...');
    });
    this.userManager.events.addUserLoaded((user) => {
      this.setUserAuthData(user);
    });
  }

  async setUser(settings: User): Promise<void> {
    const user: User = new User(settings);
    await this.userManager.storeUser(user);
    this.setUserAuthData(user);
  }

  setUserAuthData(authData: User | null, authErrorState?: string | undefined): void {
    const authorizationState = { ...authData, isLogged: true, authErrorState };
    AuthorizationModuleFacade.setUserAuth(authorizationState);
  }

  startSignInMainWindow(): void {
    this.userManager.clearStaleState().then(() => {
      const redirectName = getQueryVariable('redirect');
      const redirectUrl = window.location.href;
      sessionStorage.setItem(redirectKey.redirectUrl, redirectUrl);
      const state = { redirectName };
      sessionStorage.setItem('redirectUrl', window.location.href);
      this.userManager
        .signinRedirect({ state, redirectMethod: 'replace' })
        .catch((error) => {
          console.warn(error);
        });
    });
  }

  endSignInMainWindow(): Promise<void | User> {
    return this.userManager.clearStaleState().then(() => {
      return this.userManager
        .signinCallback()
        .then((user) => {
          if (user) {
            this.setUserAuthData(user);
          }
          return user;
        });
    });
  }

  isValid(expiresAt: number): boolean {
    return new Date(expiresAt * 1000).getTime() > Date.now();
  }

  clearCodeParam(): void {
    const storedRedirectUrl = sessionStorage.getItem(redirectKey.redirectUrl);
    window.history.replaceState(
      null,
      document.title,
      storedRedirectUrl ? storedRedirectUrl : window.location.pathname
    );
    sessionStorage.removeItem(redirectKey.redirectUrl);
  }

  async validateUserAuthorization(slang?: string | undefined): Promise<void> {
    const response = await userClient.checkUserAuthorization2();

    if(!response.isAuthorized) {
      storeLanguage(slang ? getLanguageSubtagFromBCP47LanguageTag(slang) : INITIAL_LANGUAGE);
      throw response.authErrorCode;
    }
  }

  async authorize(codeParamValue?: string | null): Promise<AuthorizationResult | null> {
    if (codeParamValue) {
      AuthorizationModuleFacade.clearAuthorizationState();
      const response = await this.endSignInMainWindow()
        .then(async (user) => {
          const profile = (user as User)?.profile as Profile;
          await this.validateUserAuthorization(profile?.slng);
          
          this.clearCodeParam();

          return user;
        })
        .catch((error) => {
          this.clearCodeParam();
          console.warn(error);
          /**
           * isNoMatchingStateError:
           * issue happens when user trying to connect with old link containing a code param, in that case
           * 'No matching state found in storage' error will be catched, because OIDC didnt register starting state
           * in such  case we need to call startSignInMainWindow()
           *
           * isClockSkewErrorFuture: When user clock is set 5min+ in the future
           * isClockSkewErrorPast: When user clock is set 5min+ in the past
           */
          const errorMessage = error.toString();
          const isNoMatchingStateError = errorMessage.includes('No matching state found in storage');
          const isClockSkewErrorFuture = errorMessage.includes('exp is in the past');
          const isClockSkewErrorPast = errorMessage.includes('iat is in the future');
          const iNoStateInResponse = errorMessage.includes('No state in response');

          sendExceptionEvent({ exception: error, severityLevel: SeverityLevel.Error, properties: { 'incorrect_person': true }});

          if (isNoMatchingStateError || iNoStateInResponse) {
            this.startSignInMainWindow();
            return { success: false };
          }

          if(isClockSkewErrorFuture) {
            throw AuthErrorCode.ClockSkew;
          }

          if(isClockSkewErrorPast) {
            throw AuthErrorCode.ClockSkew;
          }

          throw error;
        });

      return {
        success: true,
        profile: (response as User)?.profile as Profile,
        access_token: (response as User)?.access_token,
      };
    }

    // else start sign IN
    AuthorizationModuleFacade.clearAuthorizationState();
    this.startSignInMainWindow();
    return null;
  }

  async logout(logoutType: LogoutType = LogoutType.ManualLogout): Promise<void> {
    await this.userManager.removeUser();
    await this.userManager.signoutRedirect({
      extraQueryParams: {
        logoutType,
      },
    });
  }
}
